/**
 * Invoice-specific utility functions for variable replacement
 */

import { replaceVariables, formatCurrency, formatDate, getCurrencySymbol, getPhoneNumbers, formatDateLong } from './variable-replacer';
import utils from "@/utils";
import Vue from "vue";



export function prepareOrderData(order, billingAccount, translate) {
    if (!order) {
        return {};
    }

    // Use provided items or fallback to order.items_count for count
    const itemsCount = order.items_count || 0;
    const data = {
        order_id: order.order_id,
        order_date: order.validated_date,
        delivery_date: formatDate(order.delivery_date),
        delivery_date_long: formatDateLong(order.delivery_date),

        total_amount_excl_vat: formatCurrency(order.total_amount_excl_vat || order.total_excl_vat, order.currency),
        total_amount_incl_vat: formatCurrency(order.total_amount_incl_vat || order.total_incl_vat, order.currency),
        total_vat_amount: formatCurrency(order.total_vat_amount || order.vat_amount, order.currency),
        currency: order.currency || 'EUR',
        currency_symbol: getCurrencySymbol(order.currency || 'EUR'),

        buyer: order.buyer_data?.name || order.buyer?.name || '',
        buyer_name: order.buyer_data?.name || order.buyer?.name || '',
        billed: order.buyer_data?.name || order.buyer?.name || '',
        billed_name: order.buyer_data?.name || order.buyer?.name || '',
        billing_address: order.buyer_data?.billing_address?.full || order.buyer?.billing_address?.full || '',
        contact_email: order.buyer_data?.billing_address?.emails?.[0] || order.buyer?.billing_address?.emails?.[0] || '',
        contact_phone_numbers: getPhoneNumbers(order.buyer_data?.billing_address),
        agent: order.agent_name || '',
        agent_name: order.agent_name || '',

        seller: billingAccount?.name || '',
        seller_name: billingAccount?.name || '',
        seller_contact_email: billingAccount?.address?.emails?.[0] || '',
        seller_contact_phone_numbers: getPhoneNumbers(billingAccount?.address),
        seller_address: billingAccount?.address?.full || '',

        // Items
        items_count: itemsCount,
    };

    return data;
}

export function getOrderVariables(order, billingAccount) {

    // Calculate values once to avoid recalculation on every render
    const currencySymbol = getCurrencySymbol(order.currency || "EUR");

    return [
        {
            name: "order",
            label: Vue.prototype.$translate("variables.order"),
            variables: [
                {
                    value: order.order_id,
                    label: Vue.prototype.$translate("variables.orderId"),
                },
                {
                    value: formatDateLong(order.validated_date),
                    label: Vue.prototype.$translate("variables.validatedDate"),
                },
                {
                    value:
                        formatCurrency(order.total_amount_excl_vat || order.total_excl_vat, order.currency),
                    label: Vue.prototype.$translate("variables.totalExclVat"),
                },
                {
                    value:
                        formatCurrency(order.total_amount_incl_vat || order.total_incl_vat, order.currency),
                    label: Vue.prototype.$translate("variables.totalInclVat"),
                },
                {
                    value: formatCurrency(order.total_vat_amount || order.vat_amount, order.currency),
                    label: Vue.prototype.$translate("variables.totalVatAmount"),
                },
                {
                    value: order.currency || "EUR",
                    label: Vue.prototype.$translate("variables.currency"),
                },
                {
                    value: currencySymbol,
                    label: Vue.prototype.$translate("variables.currencySymbol"),
                },
                {
                    value: order.items_count || 0,
                    label: Vue.prototype.$translate("variables.itemsCount"),
                },
            ],
        },
        {
            name: "issuer",
            label: Vue.prototype.$translate("variables.seller"),
            variables: [
                {
                    value: billingAccount?.name || "",
                    label: Vue.prototype.$translate("variables.sellerName"),
                },
                {
                    value: billingAccount?.address?.emails?.[0] || "",
                    label: Vue.prototype.$translate("variables.sellerContactEmail"),
                },
                {
                    value: getPhoneNumbers(billingAccount?.address),
                    label: Vue.prototype.$translate("variables.sellerContactPhoneNumbers"),
                },
                {
                    value: billingAccount?.address?.full || "",
                    label: Vue.prototype.$translate("variables.sellerAddress"),
                },
            ],
        },
        {
            name: "buyer",
            label: Vue.prototype.$translate("variables.buyer"),
            variables: [
                {
                    value: (order.buyer_data || order.buyer_entity_data)
                        ?.name,
                    label: Vue.prototype.$translate("variables.buyerName"),
                },
                {
                    value: (order.buyer_data || order.buyer_entity_data)
                        ?.billing_address?.full,
                    label: Vue.prototype.$translate("variables.billingAddress"),
                },
                {
                    value: (order.buyer_data || order.buyer_entity_data)
                        ?.billing_address?.emails?.[0],
                    label: Vue.prototype.$translate("variables.contactEmail"),
                },
                {
                    value: getPhoneNumbers((order.buyer_data || order.buyer_entity_data)?.billing_address),
                    label: Vue.prototype.$translate("variables.contactPhoneNumbers"),
                },
            ]
        },
    ];
}

export function replaceOrderVariables(template, order, billingAccount, translate = null) {
    const data = prepareOrderData(order, billingAccount, translate);
    return replaceVariables(template, data);
}